// New file for the improved type-safe form field creator and types
import { Paths } from '@/utils/nestedObjectPaths';
import {
  ConnectedInput,
  ConnectedInputProps,
} from '@/components/Inputs/ConnectedInput';
import { FormRow, FormRowProps } from './FormRow';

export type DummyFieldName = `@@${string}`;

type FieldNames<T> = Paths<T, 2> | DummyFieldName;

type Expand<T> = T extends infer O ? { [K in keyof O]: O[K] } : never;

type Values<T> = Expand<Record<FieldNames<T>, any>>;

export type FormValues<T> = T & Omit<Partial<Values<T>>, keyof T>;

// Common props shared by all field definitions
export type CommonFieldProps<T> = {
  name: Paths<T, 2> | DummyFieldName;
  label?: React.ReactNode;
  Icon?: React.ElementType;
  hidden?: boolean;
  disabled?: boolean;
  default?: any;
  rowComponent?: React.ComponentType<FormRowProps<any>>;
  rowProps?: Omit<FormRowProps<any>, 'name' | 'inputComponent'>;
};

// Shape when inputComponent is present
export type FieldWithConnectedInput<T, C extends React.ComponentType<any>> = {
  inputComponent: C;
  inputProps?: Omit<React.ComponentProps<C>, 'name'>;
} & CommonFieldProps<T>;

// Shape when inputComponent is not present (default Input)
export type FieldWithoutConnectedInput<T> = {
  inputComponent?: undefined;
  inputProps?: Omit<ConnectedInputProps, 'name'>;
} & CommonFieldProps<T>;

export type FieldDefinition<T, P extends React.ComponentType<any>> =
  | FieldWithConnectedInput<T, P>
  | FieldWithoutConnectedInput<T>;

// Improved type definition to better handle function returns
export type FieldFunction<T, P extends React.ComponentType<any>> = (
  values: FormValues<T>,
  index?: number
) => FieldDefinition<T, P> | undefined | false;

export type DefiniteFormFieldDefinition2<
  T,
  P extends React.ComponentType<any>,
> = FieldDefinition<T, P> | FieldFunction<T, P>;

// Overload-based helper for type-safe field creation
export function makeFormFieldCreator<T = {}>() {
  // Overload for field definition WITHOUT inputComponent (defaults to ConnectedInput)
  function creator(
    def: FieldWithoutConnectedInput<T> | undefined | false
  ): (
    values: FormValues<T>
  ) => FieldWithConnectedInput<T, typeof ConnectedInput> | undefined;

  // Overload for field definition WITH inputComponent
  function creator<P extends React.ComponentType<any>>(
    def: FieldWithConnectedInput<T, P> | undefined | false
  ): (values: FormValues<T>) => FieldWithConnectedInput<T, P> | undefined;

  // Overload for union FieldDefinition
  function creator<P extends React.ComponentType<any>>(
    def: FieldDefinition<T, P> | undefined | false
  ): (values: FormValues<T>) => FieldWithConnectedInput<T, P> | undefined;

  // Overload for function field definition
  function creator<P extends React.ComponentType<any>>(
    def: FieldFunction<T, P> | undefined | false
  ): (values: FormValues<T>) => FieldWithConnectedInput<T, P> | undefined;

  // Implementation
  function creator<P extends React.ComponentType<any>>(
    def:
      | FieldWithoutConnectedInput<T>
      | DefiniteFormFieldDefinition2<T, P>
      | undefined
      | false
  ) {
    return function (values: FormValues<T>, index?: number) {
      let defValues = typeof def === 'function' ? def(values, index) : def;
      if (!defValues) return undefined;
      if (!defValues.rowComponent) {
        defValues.rowComponent = FormRow;
      }
      if (!defValues.inputComponent) {
        // If inputComponent is not provided, use Input and InputProps
        return {
          ...defValues,
          inputComponent: ConnectedInput as any,
          inputProps: defValues.inputProps as Omit<ConnectedInputProps, 'form'>,
        } as FieldWithConnectedInput<
          T,
          React.FC<Omit<ConnectedInputProps, 'form'>>
        >;
      }
      return defValues as FieldWithConnectedInput<T, P>;
    };
  }

  return creator;
}
